<script setup lang="ts">
import { Bookmark, Share, XMark, Magazine } from "../icons";
import ArticleContentView from "./ArticleContentView.vue";
import ArticleRelatedArticles from "./ArticleRelatedArticles.vue";
import ArticleMagazine from "./ArticleMagazine.vue";
import { useQuery } from "@tanstack/vue-query";
import { ArticleInclude, getArticle, pushAppEvents } from "../../repository";
import { computed, toRef, ref, watch } from "vue";
import { useArticleTimeAgo } from "../../lib/article";
import { Clock } from "../icons";
import Modal from "../Modal.vue";
import ShareModal from "../ShareModal.vue";
import { useHead } from "@unhead/vue";
import { AppEvent } from "../../types/appEvents";
import { notify } from "@kyvg/vue3-notification";
import { useRouter } from "vue-router";
import ArticleNoAccess from "./ArticleNoAccess.vue";
import LoadingIndicator from "../general/LoadingIndicator.vue";
import { ErrorState } from "../states";
import { useI18n } from "vue-i18n";
import { AnalyticsEvent, useAnalytics } from "../../lib/analytics";
import logger from "../../lib/logger";

const { t } = useI18n();
const { logEvent } = useAnalytics();

const props = defineProps<{
  articleId: string | number;
}>();

const router = useRouter();
const openShareModal = ref(false);
const loadingBookmark = ref(false);

const { data, isLoading, isError, error, refetch } = useQuery({
  queryKey: ["article", toRef(props, "articleId")],
  queryFn: () =>
    getArticle(props.articleId as string, {
      include: [
        ArticleInclude.Edition,
        ArticleInclude.FirstPage,
        ArticleInclude.EndUserArticleData,
        ArticleInclude.EndUserSubscription,
      ],
    }),
});

const article = computed(() => data.value?.data.data);

const timeAgo = useArticleTimeAgo(article);

const readTime = computed(() => {
  if (!article.value) return null;
  return Math.round(article.value.reading_seconds / 60) + " min";
});

const editionPath = computed(() => {
  if (!article.value?.edition) return null;
  let url = `/edition/${article.value.edition.id}`;
  if (article.value.first_page?.number) {
    url += `?page=${article.value.first_page.number}`;
  }
  return url;
});

const isBookmarked = computed(() => {
  if (!article.value?.end_user_article_data) return false;
  return article.value.end_user_article_data?.bookmarked ?? false;
});

const toggleBookmark = async () => {
  if (!article.value || loadingBookmark.value) return;
  loadingBookmark.value = true;
  try {
    await pushAppEvents([
      {
        type: AppEvent.ArticleBookmarkModified,
        data: {
          article_id: article.value.id,
          bookmarked: !isBookmarked.value,
          bookmark_modified_at: new Date().toISOString(),
        },
      },
    ]);
    logEvent(AnalyticsEvent.ContentBookmark, {
      contentId: article.value.id,
      contentType: "article",
      contentName: article.value.name,
      bookmarked: !isBookmarked.value,
    });
    await refetch();
  } catch (err) {
    logger.error("failed to toggle bookmark", {
      articleId: article.value.id,
    });
    notify({
      type: "error",
      title: t("common.error.title"),
      text: t("common.error.description"),
    });
  } finally {
    loadingBookmark.value = false;
  }
};

useHead({
  title: () => article.value?.name ?? t("article_page.title"),
});

const handleBack = () => {
  if (router.options.history.state.back) {
    router.back();
  } else {
    router.push("/");
  }
};

const hasAccess = computed(() => {
  if (article.value) {
    const hasAccess = article.value.end_user_subscription?.has_access;
    return hasAccess;
  }
  return false;
});

watch(article, () => {
  if (hasAccess.value) {
    articleOpened();
  }
});

const articleOpened = async () => {
  try {
    await pushAppEvents([
      {
        type: AppEvent.ArticleOpened,
        data: {
          article_id: String(props.articleId),
          last_opened_at: new Date().toISOString(),
        },
      },
    ]);
  } catch (e) {
    logger.error("failed to push app event", {
      type: AppEvent.ArticleOpened,
      articleId: props.articleId,
    });
  }
};
</script>

<template>
  <div
    class="max-w-xl mx-auto py-6"
    :class="{ 'overflow-hidden h-screen grayscale': !hasAccess && article }"
  >
    <div class="flex justify-between px-4">
      <div class="flex" :class="{ 'opacity-20': !hasAccess }">
        <button
          class="btn-round-secondary mr-2"
          :disabled="!hasAccess"
          @click="toggleBookmark"
        >
          <LoadingIndicator v-if="loadingBookmark" size="sm" />
          <Bookmark v-else :weight="isBookmarked ? 'fill' : 'regular'" />
        </button>
        <button
          class="btn-round-secondary"
          :disabled="!hasAccess"
          @click="openShareModal = true"
        >
          <Share />
        </button>
      </div>
      <div class="flex">
        <RouterLink
          :to="editionPath ?? '#'"
          class="btn-round-secondary mr-2"
          :class="{ 'opacity-20': !editionPath }"
          :disabled="!editionPath"
        >
          <Magazine />
        </RouterLink>
        <button @click="handleBack" class="btn-round-secondary">
          <XMark />
        </button>
      </div>
    </div>
    <div v-if="isLoading || isError" class="min-h-80 flex-center">
      <LoadingIndicator v-if="isLoading" class="text-icon-brand" />
      <ErrorState
        v-if="isError"
        :error="error"
        @refetch="refetch"
        :contentName="$t('common.article')"
      />
    </div>
    <div v-else-if="article">
      <div class="px-4 mt-6">
        <h1 class="text-2xl font-bold">{{ article.name }}</h1>
        <div
          class="flex justify-between items-center text-text-tertiary font-sm gap-4 leading-4 mt-4"
        >
          <div class="flex items-center shrink-0">
            <p class="mr-1">{{ readTime }}</p>
            <Clock size="18" class="text-icon-secondary" />
          </div>
          <p class="text-right">{{ timeAgo }}</p>
        </div>
      </div>
      <img
        class="bg-background-tertiary object-cover mt-4"
        :src="article.thumbnail_url"
        width="910"
        height="640"
      />
      <div class="p-4">
        <ArticleContentView :zipUrl="article.zip_url" :queryKey="article.id" />
        <ArticleMagazine
          v-if="article.edition"
          class="mt-12"
          :edition="article.edition"
          :edtionPath="editionPath!"
        />
        <div class="mt-12" v-if="hasAccess">
          <h3 class="text-xl font-semibold pb-2 border-b border-border-primary">
            {{ $t("article_page.also_read") }}
          </h3>
          <ArticleRelatedArticles class="mt-4" :articleId="article.id" />
        </div>
      </div>
    </div>
    <Modal v-model="openShareModal">
      <ShareModal
        @click="openShareModal = false"
        type="article"
        :article="article"
      />
    </Modal>
  </div>
  <ArticleNoAccess v-if="article && !hasAccess" />
</template>
